import { NODE_TYPES, EDGE_TRANSITION_TYPES } from './state-tree.generator'
import { PREVIEW_ITEM, SECTION_FOCUSES } from '@/store/constants'
import chatBubbleModule from '@/store/modules/chat-bubble.module'
import conversationModule from '@/store/modules/conversation.module'
import isEmpty from 'ramda/src/isEmpty'
import { ANSWER_END, INPUT_TYPES } from '@/utils/bot-json.constants'

class StateTreeUtils {

  /**
   * @param {Object} stateTree
   * @param {Object} botJson
   * @param {String} removeStateId
   *
   * @returns {Array<String>} result.states - array of stateIds of states that will be deleted from botJson.states. Can contain duplicate stateIds.
   * @returns {Array<String>} result.postbacks - array of postback keys that will be deleted from botJson.postbacks
   * @returns {Object} result.moveTransitions - specifies that transitions should be moved from one state to another before deleting states. Has two keys - from, to. The keys might be missing, in that case, the transitions will not be moved.
   * @returns {String} result.nextStateId - id of a state that naturally follows after the last template ref state
   */
  findTemplateRefToRemove(stateTree, botJson, removeStateId) {
    const templateName = botJson.states[removeStateId].fromTemplate
    const templateRefSuffix = botJson.states[removeStateId].postbackSuffix

    const result = {
      states: [],
      postbacks: [],
      firstTemplateNode: {},
      moveTransitions: {}
    }

    this._findItemsToRemove(stateTree, botJson, templateName, templateRefSuffix, result)
    return result
  }

  _findItemsToRemove(node, botJson, templateName, templateRefSuffix, result, removeNode = false) {
    if([NODE_TYPES.FIND_INTENT, NODE_TYPES.CONTINUE_OR_FIND_INTENT, NODE_TYPES.IDLE, NODE_TYPES.CONTINUE_CONDITION, NODE_TYPES.EMPTY].includes(node.type)) {
      return
    }

    const isTemplate = this._nodeIsTemplate(node, templateName, templateRefSuffix, botJson)

    if(removeNode || isTemplate) {
      if (isEmpty(result.states)) {
        // set first template node to get section focus of its parent node
        result.firstTemplateNode = node
      }
      result.states.push(node.stateId)
    }

    if(node.type === NODE_TYPES.CONDITION) {
      if(removeNode || isTemplate) {
        result.states.push(node.conditionEndStateId)
      }
      for(const conditionCase of node.cases) {
        this._findItemsToRemove(conditionCase.node, botJson, templateName, templateRefSuffix, result, isTemplate)
      }
    }

    if (node.answerId) {
      result.moveTransitions.fromNode = node.parent.node
      return
    }

    for(const childEdge of node.children) {
      // set toNode when: currentNode IS NOT a template && childNode IS a template
      if(!isTemplate && this._nodeIsTemplate(childEdge.node, templateName, templateRefSuffix, botJson)) {
        if(childEdge.transition.type === EDGE_TRANSITION_TYPES.POSTBACK) {
          result.postbackRoot = childEdge.node.stateId
        } else {
          result.moveTransitions.toNode = node
        }
      }

      // set fromNode when: currentNode IS a template && childNode IS NOT a template
      if(isTemplate && !this._nodeIsTemplate(childEdge.node, templateName, templateRefSuffix, botJson) &&
        [
          NODE_TYPES.OUTPUT, NODE_TYPES.REMOTE, NODE_TYPES.FIND_INTENT, NODE_TYPES.CONTINUE_OR_FIND_INTENT, NODE_TYPES.IDLE, NODE_TYPES.CONDITION, NODE_TYPES.CONTINUE_CONDITION, NODE_TYPES.SAVE_CONTEXT, NODE_TYPES.CHAT_GPT
        ].includes(childEdge.node.type) && !removeNode
      ) {
        if (!result.moveTransitions.toNode && childEdge.node.conditionNode && !childEdge.node.conditionNode.isTemplate) {
          // set toNode when: conditionSection IS NOT a template && conditionCaseNode IS a template
          result.moveTransitions.toNode = childEdge.node.conditionNode
        }
        if (result.moveTransitions.toNode) {
          result.moveTransitions.fromNode = node
        }
        result.nextStateId = childEdge.node.stateId
      }

      if(childEdge.transition.type === EDGE_TRANSITION_TYPES.POSTBACK) {
        if(isTemplate) {
          result.postbacks.push(childEdge.transition.postback)
        }
        this._findItemsToRemove(childEdge.node, botJson, templateName, templateRefSuffix, result, isTemplate)
      } else if(childEdge.transition.type === EDGE_TRANSITION_TYPES.QUICK_REPLY) {
        this._findItemsToRemove(childEdge.node, botJson, templateName, templateRefSuffix, result, isTemplate)
      } else {
        this._findItemsToRemove(childEdge.node, botJson, templateName, templateRefSuffix, result, removeNode)
      }

    }
  }

  nodeIsPostback(node) {
    return node.parent &&
      node.parent.transition &&
      node.parent.transition.type === EDGE_TRANSITION_TYPES.POSTBACK
  }

  nodeIsQuickReply(node) {
    return node.parent &&
      node.parent.transition &&
      node.parent.transition.type === EDGE_TRANSITION_TYPES.QUICK_REPLY
  }

  _nodeIsTemplate(node, templateName, templateRefSuffix, botJson) {
    return [
      NODE_TYPES.OUTPUT, NODE_TYPES.CONDITION, NODE_TYPES.SAVE_CONTEXT, NODE_TYPES.VALIDATION, NODE_TYPES.REMOTE, NODE_TYPES.CHAT_GPT
    ].includes(node.type) &&
      node.isTemplate &&
      botJson.states[node.stateId].fromTemplate === templateName &&
      botJson.states[node.stateId].postbackSuffix === templateRefSuffix
  }

  findNodeByStateId(node, stateId) {
    if (node.stateId === stateId) {
      return node
    }

    if (node.type === NODE_TYPES.CONDITION) {
      for (const conditionCase of node.cases) {
        const nodeFound = this.findNodeByStateId(conditionCase.node, stateId)
        if (nodeFound) return nodeFound
      }
    }

    if (node.children) {
      for (const child of node.children) {
        const nodeFound = this.findNodeByStateId(child.node, stateId)
        if (nodeFound) return nodeFound
      }
    }

    return null
  }

  /**
   *
   * @param {Object} node is the whole stateTree (node) or a current node
   * @param {Object} bubbleSelected
   * @returns Node representing the selected bubble, or null if it is not found
   */
  findNode(node, bubbleSelected) {
    if(node.type === NODE_TYPES.EMPTY && bubbleSelected.type !== PREVIEW_ITEM.ANSWER_END) {
      return null
    }

    if (bubbleSelected.type === PREVIEW_ITEM.WAIT_FOR_INPUT) {
      if (node.stateId === bubbleSelected.stateId) {
        const transitionFound = node.children.find(childEdge => {
          return (
            childEdge.transition.type === EDGE_TRANSITION_TYPES.USER_INPUT &&
            childEdge.node.stateId === bubbleSelected.nextStateId ||
            childEdge.node.type === NODE_TYPES.SAVE_CONTEXT &&
            childEdge.node.stateId === bubbleSelected.contextStateId ||
            childEdge.node.type === NODE_TYPES.VALIDATION &&
            childEdge.node.actionId === bubbleSelected.validationActionId
          )
        })

        if (transitionFound) return node
      }
    } else if(bubbleSelected.type === PREVIEW_ITEM.BOT_BUBBLE) {
      if(node.actionId === bubbleSelected.actionId && node.stateId === bubbleSelected.stateId) {
        return node
      }
    } else if([PREVIEW_ITEM.REMOTE_BUBBLE, PREVIEW_ITEM.CHAT_GPT_BUBBLE].includes(bubbleSelected.type)) {
      if(node.actionId === bubbleSelected.actionId && node.stateId === bubbleSelected.stateId) {
        return node
      }
    } else if(bubbleSelected.type === PREVIEW_ITEM.ANSWER_END) {
      if([NODE_TYPES.IDLE, NODE_TYPES.FIND_INTENT, NODE_TYPES.CONTINUE_OR_FIND_INTENT, NODE_TYPES.CONTINUE_TEMPLATE, NODE_TYPES.CONTINUE_CONDITION, NODE_TYPES.CONTINUE_ANSWER].includes(node.type) &&
        bubbleSelected.value === this.resolveAnswerEndValue(node.type, node.parent.transition.type) &&
        !!bubbleSelected.isPostback === !!node.isPostback &&
        !!bubbleSelected.isQuickReply === !!node.isQuickReply &&
        (
          node.parent.node.stateId === bubbleSelected.stateId ||
          ((bubbleSelected.isPostback || bubbleSelected.isQuickReply) && [node.stateId, node.parent.transition.payload].includes(bubbleSelected.stateId))
        )
      ) {
        return node
      }
    } else if(bubbleSelected.type === PREVIEW_ITEM.CONDITION_SECTION) {
      if(node.stateId === bubbleSelected.stateId) {
        return node
      }
    }

    if(node.type === NODE_TYPES.CONDITION) {
      for(const conditionCase of node.cases) {
        const nodeFound = this.findNode(conditionCase.node, bubbleSelected)
        if(nodeFound) {
          return nodeFound
        }
      }
    }

    if (node.children) {
      for(const child of node.children) {
        const nodeFound = this.findNode(child.node, bubbleSelected)
        if(nodeFound) {
          return nodeFound
        }
      }
    }

    return null
  }

  resolveAnswerEndValue(value, inputType) {
    return inputType === INPUT_TYPES.USER_INPUT && value === NODE_TYPES.IDLE ?
      ANSWER_END.WAIT_INPUT_THEN_IDLE :
      value
  }

  selectEmpty() {
    chatBubbleModule.selectBotBubble(null, 'root', undefined, false)
  }

  selectNode(node, sectionFocus = SECTION_FOCUSES.NONE) {
    switch (node.type) {
      case NODE_TYPES.OUTPUT:
        chatBubbleModule.selectBotBubble(node.actionId, node.stateId, sectionFocus, node.isTemplate)
        return
      case NODE_TYPES.REMOTE:
        chatBubbleModule.selectRemoteBubble(node.stateId, node.actionId, node.isTemplate)
        return
      case NODE_TYPES.CHAT_GPT:
        chatBubbleModule.selectChatGPTBubble(node.stateId, node.actionId, node.isTemplate)
        return
      case NODE_TYPES.CONDITION:
        chatBubbleModule.selectConditionSection(node.stateId, chatBubbleModule.getConditionCaseSelected(node.stateId), node.isTemplate)
        return
      case NODE_TYPES.EMPTY:
        chatBubbleModule.selectBotBubble()
        return
      case NODE_TYPES.CONTINUE_CONDITION:
        chatBubbleModule.selectAnswerEnd(node.stateId, node.type, node.isTemplate, node.isCondition, node.conditionNode.conditionEndStateId)
        return
      case NODE_TYPES.VALIDATION:
        const { nextStateId, contextStateId } = this._resolveSelectWaitForInputParams(node)
        chatBubbleModule.selectWaitForInput(node.stateId, nextStateId, node.actionId, contextStateId, node.isTemplate)
        return
      case NODE_TYPES.CONTINUE_TEMPLATE:
      case NODE_TYPES.CONTINUE_ANSWER:
      case NODE_TYPES.FIND_INTENT:
      case NODE_TYPES.CONTINUE_OR_FIND_INTENT:
      case NODE_TYPES.IDLE:
        chatBubbleModule.selectAnswerEnd(node.stateId, node.type, node.isTemplate, false, null, node.isPostback, node.isQuickReply, false, node.answerId)
        return
      default:
        console.error('selectNode - unknown node type:', node.type)
    }
  }

  findTemplateStateIds(botJsonStates, node, templateStateIds = []) {
    if([NODE_TYPES.FIND_INTENT, NODE_TYPES.CONTINUE_OR_FIND_INTENT, NODE_TYPES.IDLE, NODE_TYPES.CONTINUE_CONDITION, NODE_TYPES.CONTINUE_ANSWER, NODE_TYPES.EMPTY].includes(node.type)) {
      return templateStateIds
    }

    if(node.isTemplate && !node.answerId) {
      const templateStateId = botJsonStates[node.stateId].fromTemplate.replace('ref:template:', '')
      if(!templateStateIds.includes(templateStateId)) {
        templateStateIds.push(templateStateId)
      }
    }

    if(node.type === NODE_TYPES.CONDITION) {
      for(const conditionCase of node.cases) {
        this.findTemplateStateIds(botJsonStates, conditionCase.node, templateStateIds)
      }
    }

    if (node.children) {
      for(const childEdge of node.children) {
        this.findTemplateStateIds(botJsonStates, childEdge.node, templateStateIds)
      }
    }

    return templateStateIds
  }

  _resolveSelectWaitForInputParams(validatedInputNode) {
    const childNode = validatedInputNode.children[0].node

    const isWithContext = childNode.type === NODE_TYPES.SAVE_CONTEXT
    const nextNode = isWithContext ? childNode.children[0].node : childNode
    const contextStateId = isWithContext ? childNode.stateId : null

    const endNodeTypes = [NODE_TYPES.FIND_INTENT, NODE_TYPES.CONTINUE_OR_FIND_INTENT, NODE_TYPES.IDLE, NODE_TYPES.CONTINUE_CONDITION, NODE_TYPES.CONTINUE_TEMPLATE, NODE_TYPES.CONTINUE_ANSWER]
    const nextStateId = endNodeTypes.includes(nextNode.children[0].node.type) ?
      conversationModule.getFirstStateTransition(childNode.stateId)?.state :
      nextNode.children[0].node.stateId

    return { nextStateId, contextStateId }
  }
}

export default new StateTreeUtils()
